import { AxiosRequestConfig, CancelToken } from 'axios';
import dateFormat from 'dateformat';
import moment from 'moment';
import { Employee, User } from './AccountsAPI';
import AxiosConfig from './AxiosConfig';
import { getUTCDateString } from './Functions';
import { getWeekEndingDate, isNumeric, parseStringNumber } from './TSFunctions';

const axiosInstance = AxiosConfig.instance();

/*-------------------------------Jobs-------------------------------------*/

export function getAllJobs(includeClosedJobs: boolean = false, token?: CancelToken): Promise<CYMAJob[]> {
  var url = '/cyma/jobs';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
    config.params = {} as any;
  }

  if (includeClosedJobs !== undefined) config.params.includeClosedJobs = includeClosedJobs;

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getJob(jobNumber: string, getFullJob: boolean = false, token?: CancelToken): Promise<CYMAJob | FullCYMAJob> {
  var url = '/cyma/jobs/' + jobNumber;

  var config: AxiosRequestConfig = { params: {} };
  if (token !== undefined) {
    config.cancelToken = token;
  }

  if (getFullJob !== undefined) config.params.getFullJob = getFullJob;

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function putJob(jobNumber: string, visibleUntilDate: Date, token?: CancelToken): Promise<CYMAJob> {
  var url = '/cyma/jobs/' + jobNumber;

  var config: any = { params: {} };
  if (token !== undefined) {
    config.cancelToken = token;
  }

  if (visibleUntilDate !== undefined) config.params.visibleUntilDate = moment(visibleUntilDate).toISOString();

  return axiosInstance.put(url, config).then((response) => response.data);
}

export function getUsersForJob(jobNumber: string, token?: CancelToken): Promise<Employee[]> {
  var url = '/cyma/jobs/' + jobNumber + '/accounts';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data.map((d) => new Employee(d)));
}

export function getUserAssignedJobs(
  userId: string,
  includeClosedJobs?: boolean,
  includeClosedCostCodes?: boolean,
  fullJob?: boolean,
  token?: CancelToken
): Promise<CYMAJob[]> {
  var url = '/accounts/' + userId + '/jobs';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
    config.params = {} as any;
  }

  if (includeClosedJobs !== undefined) config.params.includeClosedJobs = includeClosedJobs;
  if (includeClosedCostCodes !== undefined) config.params.includeClosedCostCodes = includeClosedCostCodes;
  if (fullJob !== undefined) config.params.fullJob = fullJob;

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getSchedulerJobAssignments(userId: string, periodEndingDate: Date, token?: CancelToken): Promise<CYMAJob[]> {
  var url = `/schedule/${userId}/jobs`;

  var config: AxiosRequestConfig = { params: {} };
  if (token !== undefined) {
    config.cancelToken = token;
  }

  if (periodEndingDate !== undefined) config.params.periodEndingDate = getUTCDateString(getWeekEndingDate(periodEndingDate));

  return axiosInstance.get(url, config).then((response) => response.data.map((job) => ({ ...job, isSchedule: true })));
}

export function putUserJobs(userId: string, jobs: CYMAJob[]): Promise<any[]> {
  const url = `accounts/${userId}/jobs`;
  return axiosInstance.put(url, jobs).then((response) => response.data);
}

export function postUserJob(userId: string, job: CYMAJob): Promise<any[]> {
  const url = `accounts/${userId}/jobs`;
  return axiosInstance.post(url, job).then((response) => response.data);
}

export function deleteUserJob(userId: string, job: CYMAJob): Promise<any[]> {
  const url = `accounts/${userId}/jobs`;
  const config = { data: job };
  return axiosInstance.delete(url, config).then((response) => response.data);
}

export function postJobAccessRequest(jobNumber: string, message: string): Promise<void> {
  const url = `cyma/jobs/${jobNumber}/access`;
  return axiosInstance.post(url, { message }).then((response) => response.data);
}

/*-------------------------------Cost Codes-------------------------------------*/

export function getCostCodesforJob(jobNumber: string, token?: CancelToken): Promise<CostCode[]> {
  const url = '/cyma/jobs/' + jobNumber + '/costcodes';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function putCostCodeDescriptionAndClosed(code: CostCode): Promise<CostCode> {
  const url = `cyma/jobs/${code.jobNumber}/costcodes/${code.code}`;

  var body = new FormData();
  body.append('description', code.description);
  body.append('dateClosed', code.dateClosed ? dateFormat(code.dateClosed, 'isoUtcDateTime') : '');

  var config: any = { headers: { 'Content-Type': 'multipart/form-data' } };

  return axiosInstance.put(url, body, config).then((response) => response.data);
}

export function putUserCostCodes(userId: string, codes: CostCode[]): Promise<any> {
  const url = `accounts/${userId}/costcodes`;
  return axiosInstance.put(url, codes).then((response) => response.data);
}

export function postUserCostCode(userId: string, code: CostCode): Promise<any> {
  const url = `accounts/${userId}/costcodes`;
  return axiosInstance.post(url, code).then((response) => response.data);
}

export function deleteUserCostCode(userId: string, code: CostCode): Promise<any[]> {
  const url = `accounts/${userId}/costcodes`;
  const config = { data: code };
  return axiosInstance.delete(url, config).then((response) => response.data);
}

/*-------------------------------Earnings Types-------------------------------------*/
export function getEarningsTypesForUser(userId: string, token?: CancelToken): Promise<EarningsType[]> {
  const url = '/accounts/' + userId + '/earningstypes';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

/*-------------------------------GL Codes-------------------------------------*/

export function getCYMAGl(token?: CancelToken): Promise<CYMAGl[]> {
  var url = '/cyma/gl';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

/*-------------------------------Skills-------------------------------------*/

export function getSkills(token?: CancelToken): Promise<Skill[]> {
  const url = '/cyma/skills';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function putSkill(skill: Skill): Promise<Skill> {
  const url = `cyma/skills`;
  return axiosInstance.put(url, skill).then((response) => response.data);
}

export function assignSkillToUser(userId: string, skill: Skill): Promise<Skill> {
  const url = `accounts/${userId}/skills`;
  return axiosInstance.post(url, skill).then((response) => response.data);
}

export function removeSkillFromUser(userId: string, skill: Skill): Promise<Skill> {
  const url = `accounts/${userId}/skills/${skill.name}`;
  return axiosInstance.delete(url).then((response) => response.data);
}

export function deleteSkill(skill: Skill): Promise<Skill | undefined> {
  const url = `cyma/skills/${skill.name}`;
  return axiosInstance.delete(url).then((response) => response.data);
}

/*-------------------------------Bill Rates-------------------------------------*/

export function getBillRates(token?: CancelToken): Promise<BillRate[]> {
  const url = '/cyma/billrates';

  var config: any = {};
  if (token !== undefined) config.cancelToken = token;

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function putBillRate(rate: BillRate, saveAsNew = false): Promise<BillRate> {
  const url = `cyma/billrates?createNew=${saveAsNew ? 'true' : 'false'}`;
  return axiosInstance.put(url, rate).then((response) => response.data);
}

export function deleteBillRate(rate: BillRate): Promise<BillRate | undefined> {
  const url = `cyma/billrates/${rate.id}`;
  return axiosInstance.delete(url).then((response) => response.data);
}

export function isBillRateValid(rate: BillRate): string[] {
  const errors: string[] = [];
  if (!rate.skill) errors.push('Bill rate must have skill!');
  if (!rate.regRate || !isNumeric(rate.regRate) || rate.regRate < 0) errors.push('Reg rate must be a valid positive number!');
  if (!rate.otRate || !isNumeric(rate.otRate) || rate.otRate < 0) errors.push('OT rate must be a valid positive number!');
  return errors;
}

export const validateRate = (rate?: string | number) => {
  return rate === null || rate === undefined || rate === '' || (isNumeric(rate) && parseStringNumber(rate) >= 0);
};

/*-------------------------------Customer-------------------------------------*/

export function getCustomers(token?: CancelToken): Promise<Customer[]> {
  const url = '/cyma/customers';

  var config: any = {};
  if (token !== undefined) config.cancelToken = token;

  return axiosInstance.get(url, config).then((response) => response.data);
}

export interface CYMAJob {
  jobNumber: string;
  desc?: string;
  typeId?: string;
  custId?: string;
  custName?: string;
  manager?: string;
  startDate?: Date;
  endDate?: Date;
  open?: boolean;
  visibleUntil?: Date;

  contractVal?: number;
  billToDate?: number;
  costToDate?: number;
  pymtToDate?: number;

  //location
  locationName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;

  isSchedule?: boolean;

  users?: User[];
  costCodes?: CostCode[];
}

export interface FullCYMAJob extends CYMAJob {
  masterJobId?: string;
  futureField?: string;
  contractNo?: string;
  orderNo?: string;
  billing?: number;
  bdgtToDate?: number;
  chargeCost?: number;
  dmPct?: number;
  dlPct?: number;
  subPct?: number;
  odcPct?: number;
  ovhPct?: number;
  unkPct?: number;
  costPct?: number;
  budgetType?: number;
  segment1?: string;
  segment2?: string;
  segment3?: string;
  segment4?: string;
  segment5?: string;
  applyGa?: boolean;
  exceedContract?: boolean;
  applyOvh?: boolean;
  invoiceBy?: number;
  useSalesAccount?: number;
  invoiceFee?: number;
  fee?: boolean;
  fixedFee?: number;
  feeProdId?: string;
  changeOrd?: number;
  invoiceDl?: number;
  markUpBy?: number;
  begInvToDate?: number;
  begPymToDate?: number;
  retProd?: string;
  retPct?: number;
  locationId?: string;
  costCode?: boolean;
  ref1?: string;
  ref2?: string;
  ref3?: string;
  sellerId?: string;
  certified?: boolean;
  groupId?: string;
  unused?: string;
  createdBy?: string;
  updatedBy?: string;
  lastModified?: Date;
}

export interface EarningsType {
  vwId: number;
  empId: number;
  typeRcdId: number;
  longName: string;
  shortName: string;
  includeInWebPortal: boolean;
  canReplaceJob: boolean;
  status: number;
}

export interface CostCode {
  jobNumber: string;
  code: string;
  description: string;
  dateClosed: Date;
  revAmt?: number;
  budgAmt?: number;
  contractAmt?: number;

  //injected
  totalInvoiced?: number;
  materialCost?: number;
  openPOValue?: number;
  laborCostBillable?: number;
  laborCostPay?: number;
  otherCost?: number;

  createdBy?: string;
  createdDate?: Date;
  updatedBy?: string;
  updatedDate?: Date;
  budgUnits?: number;
  unitCost?: number;
  billChgOrd?: number;
  nonBillChgOrd?: number;
  fldMeasure?: number;
  estToComplete?: number;
  markUpPct?: number;
  billable?: boolean;
  estUnits?: number;
  compUnits?: number;
  unused?: string;

  users: User[];
}

export function totalCCCost(cc?: CostCode, isAdmin: boolean = false) {
  return (cc?.openPOValue ?? 0) + (cc?.materialCost ?? 0) + (cc?.otherCost ?? 0) + (isAdmin ? cc?.laborCostPay ?? 0 : cc?.laborCostBillable ?? 0);
}

export interface CYMAGl {
  account: string;
  status: string;
  description: string;
  retEarnAcct: string;
  accountType: string;
  statementId: string;
  ratioId: string;
  classType: string;
  debitBoolean: boolean;
  cashflowType: string;
  createUserId: string;
  noteAutoPopup: boolean;
  segment1: string;
  segment2: string;
  segment3: string;
  segment4: string;
  segment5: string;
  segment6: string;
  segment7: string;
  segment8: string;
  segment9: string;
  segment10: string;
  accntIdStr: string;
  bUnused: boolean;
  subType: string;
  tdc: string;
  subTdc: string;
  activity: string;
  property: string;
  subTdcDesc: string;
  encumber: string;
  parentAccountId: string;
  coe: string;
  fundSrc: string;
  printSummary: boolean;
  createdDate: Date;
  updatedDate: Date;
  costRptId: string;
  unused: string;
}

export interface Skill {
  name: string;
  description?: string;
}

export interface BillRate {
  id?: number;
  skill?: string; //required
  regRate?: number; //required
  otRate?: number; //required
  custId?: string;
  jobNumber?: string;
  userId?: string;
  user?: User;
  isDisabled?: boolean;
  effectiveDate?: Date;
}

export interface Customer {
  custId: string;
  method: number;
  typeId: string;
  name: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  termsCode: string;
  emailAddress: string;
}
